/**
 * challengeResultItem.helper.ts
 * Maps a completed ChallengeWithStats into a GameResultItem for display in the GameResultModal.
 * Only processes 1v1 challenges (no groupId, exactly 2 participants).
 */

import type { GameResultItem } from '../../../components/challenges/GameResultModal/gameResultModal.types';
import type { EquippedMap } from '../../../components/avatars/LayeredAvatarLab/characterCustomizer.types';
import type { ChallengeWithStats } from '../../../types/challenges';
import { DEFAULT_AVATARS } from '../../../components/challenges/GameResultModal/GameResultModal.constants';
import type { GameResultModalAvatarId } from '../../../components/challenges/GameResultModal/gameResultModal.helper';
import { normalizeAvatarId } from '../../../constants/avatarIds';
import { normalizeUserIdForComparison } from './challengeCardParticipant.helper';

type ChallengeWithMaps = ChallengeWithStats & {
  participantAvatarIdsById?: Record<string, string>;
  participantDisplayNamesById?: Record<string, string>;
  participantAvatarsById?: Record<string, string>;
  participantAvatarConfigsById?: Record<string, EquippedMap>;
};

/** Resolve map[userId] when the client user id string may not match map keys byte-for-byte. */
function pickParticipantMapValue(
  map: Record<string, string> | undefined,
  userId: string
): string | undefined {
  if (!map || Object.keys(map).length === 0) return undefined;
  const direct = map[userId];
  if (direct != null && String(direct).trim() !== '') return String(direct).trim();
  const target = normalizeUserIdForComparison(userId);
  for (const [k, v] of Object.entries(map)) {
    if (normalizeUserIdForComparison(k) === target && v != null && String(v).trim() !== '') {
      return String(v).trim();
    }
  }
  return undefined;
}

/** Resolve map[userId] for object-valued maps (e.g. avatar configs), with id-normalization fallback. */
function pickParticipantConfig(
  map: Record<string, EquippedMap> | undefined,
  userId: string
): EquippedMap | undefined {
  if (!map || Object.keys(map).length === 0) return undefined;
  const direct = map[userId];
  if (direct != null) return direct;
  const target = normalizeUserIdForComparison(userId);
  for (const [k, v] of Object.entries(map)) {
    if (normalizeUserIdForComparison(k) === target && v != null) return v;
  }
  return undefined;
}

function resolveModalAvatarId(
  raw: string | undefined,
  fallback: GameResultModalAvatarId
): GameResultModalAvatarId {
  if (raw == null || String(raw).trim() === '') return fallback;
  const trimmed = String(raw).trim();
  if (trimmed === 'monkey') return 'monkey';
  return normalizeAvatarId(trimmed);
}

/**
 * Convert a completed 1v1 challenge into a GameResultItem.
 * Returns null for group challenges or challenges with unexpected participant counts.
 */
export function buildGameResultItem(
  challenge: ChallengeWithMaps,
  currentUserId: string,
  currentUserAvatar?: { avatarId: string | null; avatarConfig: EquippedMap | null } | null,
): GameResultItem | null {
  // Only process 1v1 (no group, exactly 2 participants)
  if (challenge.groupId) return null;
  if (!challenge.participantIds || challenge.participantIds.length !== 2) return null;

  const normalizedCurrentId = normalizeUserIdForComparison(currentUserId);
  const rivalId = challenge.participantIds.find(
    (id) => normalizeUserIdForComparison(id) !== normalizedCurrentId,
  );
  if (!rivalId) return null;

  // Points — defensive fallback to 0
  const yourPoints = Number(challenge.myPoints ?? 0);

  let rivalPointsRaw = 0;
  if (
    challenge.leader?.userId &&
    normalizeUserIdForComparison(challenge.leader.userId) === normalizeUserIdForComparison(rivalId)
  ) {
    rivalPointsRaw = Number(challenge.leader.points ?? 0);
  } else {
    rivalPointsRaw = (challenge.picks ?? [])
      .filter((p) => normalizeUserIdForComparison(p.userId) === normalizeUserIdForComparison(rivalId))
      .reduce((sum, p) => sum + Number(p.pointsEarned ?? 0), 0);
  }
  const rivalPoints = Number(rivalPointsRaw);

  // Outcome — Number() cast guards against string coercion from API
  let outcome: 'won' | 'lost' | 'draw';
  if (yourPoints > rivalPoints) outcome = 'won';
  else if (yourPoints < rivalPoints) outcome = 'lost';
  else outcome = 'draw';

  // Correct guesses (hits)
  const picks = challenge.picks ?? [];
  const yourCorrectGuesses = picks.filter(
    (p) =>
      normalizeUserIdForComparison(p.userId) === normalizedCurrentId &&
      p.isCorrectOutcome === true,
  ).length;
  const rivalCorrectGuesses = picks.filter(
    (p) =>
      normalizeUserIdForComparison(p.userId) === normalizeUserIdForComparison(rivalId) &&
      p.isCorrectOutcome === true,
  ).length;

  // Avatars (preset id + optional profile photo URL from API)
  const avatarIdsById = (challenge as ChallengeWithMaps).participantAvatarIdsById ?? {};
  const avatarsById = (challenge as ChallengeWithMaps).participantAvatarsById ?? {};
  const rawHomeAvatarId = pickParticipantMapValue(avatarIdsById, currentUserId);
  const rawAwayAvatarId = pickParticipantMapValue(avatarIdsById, rivalId);
  // Your own side: prefer the LIVE profile avatar so a just-changed look shows immediately,
  // instead of the avatar baked into the (cached) challenge snapshot. Fall back to the snapshot
  // only when the live profile isn't available (e.g. profile not loaded yet).
  const homeAvatarId = currentUserAvatar
    ? resolveModalAvatarId(currentUserAvatar.avatarId ?? undefined, DEFAULT_AVATARS.HOME)
    : resolveModalAvatarId(rawHomeAvatarId, DEFAULT_AVATARS.HOME);
  const awayAvatarId = resolveModalAvatarId(rawAwayAvatarId, DEFAULT_AVATARS.AWAY);
  const homeProfileImageUri = pickParticipantMapValue(avatarsById, currentUserId);
  const awayProfileImageUri = pickParticipantMapValue(avatarsById, rivalId);

  // Custom layered looks (override preset avatarId in the modal when they have equipped layers)
  const avatarConfigsById = (challenge as ChallengeWithMaps).participantAvatarConfigsById ?? {};
  const homeAvatarConfig = currentUserAvatar
    ? currentUserAvatar.avatarConfig
    : pickParticipantConfig(avatarConfigsById, currentUserId) ?? null;
  const awayAvatarConfig = pickParticipantConfig(avatarConfigsById, rivalId) ?? null;

  // Names
  const displayNamesById = (challenge as ChallengeWithMaps).participantDisplayNamesById ?? {};
  const homeName = displayNamesById[currentUserId] ?? 'You';
  const awayName = displayNamesById[rivalId] ?? 'Rival';

  return {
    challengeId: challenge.id,
    outcome,
    coins: challenge.stakeCoins ?? 0,
    homeAvatarId,
    awayAvatarId,
    homeAvatarConfig,
    awayAvatarConfig,
    homeProfileImageUri,
    awayProfileImageUri,
    homeName,
    awayName,
    yourPoints,
    rivalPoints,
    yourCorrectGuesses,
    rivalCorrectGuesses,
  };
}
